import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  Grid,
  Snackbar,
  Alert as MuiAlert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import React, { useState, forwardRef, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MainView from "../pages/crm/MainView";
import Labels from "../labels/Labels";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import { NumericFormat } from "react-number-format";

// Format date to Java LocalDateTime compatible format: yyyy-MM-dd'T'HH:mm:ss
const formatLocalDateTime = (date) => {
  const pad = (num) => String(num).padStart(2, "0");

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

// Format date for display
const formatDateForDisplay = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

// Pending Transactions Component
export const PendingTransactions = ({ customerId, onRefresh }) => {
  // Access query client to invalidate queries
  const queryClient = useQueryClient();

  // Query to fetch pending transactions
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["pendingTransactions", customerId],
    queryFn: async () => {
      const response = await HttpService.getAxiosClient().get(
        `${window.API_URL}/member/card-funds?status=PENDING&customerId=${customerId}`
      );
      return response.data;
    },
    enabled: !!customerId, // Only run if we have a customerId
    // When pending transactions change, refresh the card info to update balance
    onSuccess: (data) => {
      // Refresh card info whenever pending transactions are updated
      queryClient.invalidateQueries(["cardview", customerId]);
      if (onRefresh) onRefresh();
    },
    refetchInterval: 30000, // Refresh every 30 seconds to check for processed transactions
    staleTime: 10000, // Consider data stale after 10 seconds
  });

  // Group transactions by type
  const pendingCredits =
    data?.filter((tx) => tx.operationType === "CREDIT") || [];
  const pendingDebits =
    data?.filter((tx) => tx.operationType === "DEBIT") || [];

  if (isLoading) {
    return <CircularProgress />;
  }

  if (isError) {
    return (
      <Alert severity="error">
        Error loading pending transactions: {error.message}
      </Alert>
    );
  }

  // If there are no pending transactions, don't render anything
  if (!data || data.length === 0) {
    return null; // Return nothing instead of a message
  }

  const renderTransactionTable = (transactions, title, chipColor) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {title} ({transactions.length})
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Amount</TableCell>
              <TableCell>Scheduled Date</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((tx) => (
              <TableRow key={tx.id}>
                <TableCell>${parseFloat(tx.amount).toFixed(2)}</TableCell>
                <TableCell>
                  {formatDateForDisplay(tx.transactionDate)}
                </TableCell>
                <TableCell>{tx.description || "N/A"}</TableCell>
                <TableCell>
                  <Chip
                    label="Pending"
                    color={chipColor}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h5">Pending Transactions</Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            refetch();
            // Also refresh card info to update balance
            queryClient.invalidateQueries(["cardview", customerId]);
            if (onRefresh) onRefresh();
          }}
          startIcon={<RefreshIcon />}
        >
          Refresh
        </Button>
      </Box>

      {pendingCredits.length > 0 &&
        renderTransactionTable(pendingCredits, "Pending Credits", "success")}
      {pendingDebits.length > 0 &&
        renderTransactionTable(pendingDebits, "Pending Debits", "error")}
    </Box>
  );
};

// Create Alert component with forwardRef
const Alert = forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Credit Dialog Component
const CreditDialog = ({ open, handleClose, customerId }) => {
  const [amount, setAmount] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedTime, setSelectedTime] = useState(
    new Date().toTimeString().split(" ")[0].substring(0, 5)
  );
  const [applyNow, setApplyNow] = useState(true);
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [description, setDescription] = useState("Card Load");

  // Setup mutation for crediting the card
  const creditMutation = useMutation({
    mutationFn: (data) => {
      return HttpService.getAxiosClient()
        .post(window.API_URL + `/member/card-funds`, data)
        .then((response) => response.data);
    },
    onSuccess: (data) => {
      setFeedback({
        open: true,
        message:
          data ||
          (applyNow
            ? `Credit operation of $${amount} applied successfully`
            : `Credit operation of $${amount} scheduled successfully`),
        severity: "success",
      });

      // Close the dialog after a short delay and reset the form
      setTimeout(() => {
        resetForm();
        handleClose();
      }, 1500);
    },
    onError: (error) => {
      console.error("Error crediting account:", error);
      setFeedback({
        open: true,
        message: error.message || "Error processing credit operation",
        severity: "error",
      });
    },
  });

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleTimeChange = (e) => {
    setSelectedTime(e.target.value);
  };

  // Reset form function to clear all inputs and feedback
  const resetForm = () => {
    setAmount("");
    setDescription("Card Load");
    setSelectedDate(new Date().toISOString().split("T")[0]);
    setSelectedTime(new Date().toTimeString().split(" ")[0].substring(0, 5));
    setApplyNow(true);
    setFeedback({
      open: false,
      message: "",
      severity: "success",
    });
  };

  // Override the handleClose function to reset the form
  const handleCloseWithReset = () => {
    resetForm();
    handleClose();
  };

  const handleSubmit = () => {
    // Validate inputs before submission
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      setFeedback({
        open: true,
        message: "Please enter a valid amount greater than 0",
        severity: "error",
      });
      return;
    }

    // If applyNow is true, use a date 1 minute in the past to trigger immediate processing
    // Otherwise use the selected date and time
    const dateTimeString = applyNow
      ? formatLocalDateTime(new Date(Date.now() - 60000)) // 1 minute in the past
      : formatLocalDateTime(new Date(`${selectedDate}T${selectedTime}:00`));

    // Prepare the request data
    const requestData = {
      customerId: customerId,
      amount: parseFloat(amount),
      transactionDate: dateTimeString,
      operationType: "CREDIT",
      description: description || "Card Load",
    };

    // Execute the mutation
    creditMutation.mutate(requestData);
  };

  const handleFeedbackClose = () => {
    setFeedback({ ...feedback, open: false });
  };

  return (
    <Dialog open={open} onClose={handleCloseWithReset} maxWidth="sm" fullWidth>
      <Box
        sx={{
          backgroundColor: "primary.main",
          px: 3,
          minHeight: 52,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AttachMoneyIcon sx={{ color: "white" }} />
          <Typography variant="h6" sx={{ color: "white" }}>
            {Labels.getLabel("manageCard.creditTitle", "Credit Money")}
          </Typography>
        </Box>
        <IconButton
          onClick={handleCloseWithReset}
          sx={{ color: "white" }}
          disabled={creditMutation.isPending}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ paddingTop: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Amount
            </Typography>
            <TextField
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount to credit"
              fullWidth
              variant="outlined"
              disabled={creditMutation.isPending}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Description
            </Typography>
            <TextField
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description for the credit"
              fullWidth
              variant="outlined"
              disabled={creditMutation.isPending}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={applyNow}
                  onChange={(e) => setApplyNow(e.target.checked)}
                  disabled={creditMutation.isPending}
                  color="primary"
                />
              }
              label={Labels.getLabel(
                "manageCard.applyNow",
                "Apply immediately"
              )}
            />
          </Grid>
          {!applyNow && (
            <>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  Date
                </Typography>
                <TextField
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={creditMutation.isPending}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  Time
                </Typography>
                <TextField
                  type="time"
                  value={selectedTime}
                  onChange={handleTimeChange}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={creditMutation.isPending}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCloseWithReset}
          color="inherit"
          disabled={creditMutation.isPending}
        >
          {Labels.getLabel("manageCard.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          color="success"
          variant="contained"
          disabled={
            creditMutation.isPending ||
            !amount ||
            amount <= 0 ||
            (!applyNow && (!selectedDate || !selectedTime))
          }
        >
          {creditMutation.isPending ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            Labels.getLabel("manageCard.submit", "Submit")
          )}
        </Button>
      </DialogActions>
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleFeedbackClose}
          severity={feedback.severity}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

// Debit Dialog Component
const DebitDialog = ({ open, handleClose, customerId }) => {
  const [amount, setAmount] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [selectedTime, setSelectedTime] = useState(
    new Date().toTimeString().split(" ")[0].substring(0, 5)
  );
  const [applyNow, setApplyNow] = useState(true);
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [description, setDescription] = useState("Card Unload");

  // Setup mutation for debiting the card
  const debitMutation = useMutation({
    mutationFn: (data) => {
      return HttpService.getAxiosClient()
        .post(window.API_URL + `/member/card-funds`, data)
        .then((response) => response.data);
    },
    onSuccess: (data) => {
      setFeedback({
        open: true,
        message:
          data ||
          (applyNow
            ? `Debit operation of $${amount} applied successfully`
            : `Debit operation of $${amount} scheduled successfully`),
        severity: "success",
      });

      // Close the dialog after a short delay and reset the form
      setTimeout(() => {
        resetForm();
        handleClose();
      }, 1500);
    },
    onError: (error) => {
      console.error("Error debiting account:", error);
      setFeedback({
        open: true,
        message: error.message || "Error processing debit operation",
        severity: "error",
      });
    },
  });

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleTimeChange = (e) => {
    setSelectedTime(e.target.value);
  };

  // Reset form function to clear all inputs and feedback
  const resetForm = () => {
    setAmount("");
    setDescription("Card Unload");
    setSelectedDate(new Date().toISOString().split("T")[0]);
    setSelectedTime(new Date().toTimeString().split(" ")[0].substring(0, 5));
    setApplyNow(true);
    setFeedback({
      open: false,
      message: "",
      severity: "success",
    });
  };

  // Override the handleClose function to reset the form
  const handleCloseWithReset = () => {
    resetForm();
    handleClose();
  };

  const handleSubmit = () => {
    // Validate inputs before submission
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      setFeedback({
        open: true,
        message: "Please enter a valid amount greater than 0",
        severity: "error",
      });
      return;
    }

    // If applyNow is true, use a date 1 minute in the past to trigger immediate processing
    // Otherwise use the selected date and time
    const dateTimeString = applyNow
      ? formatLocalDateTime(new Date(Date.now() - 60000)) // 1 minute in the past
      : formatLocalDateTime(new Date(`${selectedDate}T${selectedTime}:00`));

    // Prepare the request data
    const requestData = {
      customerId: customerId,
      amount: parseFloat(amount),
      transactionDate: dateTimeString,
      operationType: "DEBIT",
      description: description || "Card Unload",
    };

    // Execute the mutation
    debitMutation.mutate(requestData);
  };

  const handleFeedbackClose = () => {
    setFeedback({ ...feedback, open: false });
  };

  return (
    <Dialog open={open} onClose={handleCloseWithReset} maxWidth="sm" fullWidth>
      <Box
        sx={{
          backgroundColor: "primary.main",
          px: 3,
          minHeight: 52,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <MoneyOffIcon sx={{ color: "white" }} />
          <Typography variant="h6" sx={{ color: "white" }}>
            {Labels.getLabel("manageCard.debitTitle", "Debit Money")}
          </Typography>
        </Box>
        <IconButton
          onClick={handleCloseWithReset}
          sx={{ color: "white" }}
          disabled={debitMutation.isPending}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ paddingTop: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Amount
            </Typography>
            <TextField
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount to debit"
              fullWidth
              variant="outlined"
              disabled={debitMutation.isPending}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Description
            </Typography>
            <TextField
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description for the debit"
              fullWidth
              variant="outlined"
              disabled={debitMutation.isPending}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={applyNow}
                  onChange={(e) => setApplyNow(e.target.checked)}
                  disabled={debitMutation.isPending}
                  color="primary"
                />
              }
              label={Labels.getLabel(
                "manageCard.applyNow",
                "Apply immediately"
              )}
            />
          </Grid>
          {!applyNow && (
            <>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  Date
                </Typography>
                <TextField
                  type="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={debitMutation.isPending}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                  Time
                </Typography>
                <TextField
                  type="time"
                  value={selectedTime}
                  onChange={handleTimeChange}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  disabled={debitMutation.isPending}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCloseWithReset}
          color="inherit"
          disabled={debitMutation.isPending}
        >
          {Labels.getLabel("manageCard.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          color="error"
          variant="contained"
          disabled={
            debitMutation.isPending ||
            !amount ||
            amount <= 0 ||
            (!applyNow && (!selectedDate || !selectedTime))
          }
        >
          {debitMutation.isPending ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            Labels.getLabel("manageCard.submit", "Submit")
          )}
        </Button>
      </DialogActions>
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleFeedbackClose}
          severity={feedback.severity}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

// Velocity Limits Dialog Component
const VelocityLimitsDialog = ({ open, handleClose, customerId }) => {
  const queryClient = useQueryClient();
  const [dailyLimit, setDailyLimit] = useState("");
  const [monthlyLimit, setMonthlyLimit] = useState("");
  const [transactionLimit, setTransactionLimit] = useState("");
  const [originalTransactionLimit, setOriginalTransactionLimit] = useState("");
  const [expirationMinutes, setExpirationMinutes] = useState(null);
  const [pendingWorkflowStatus, setPendingWorkflowStatus] = useState(null);
  const [endDate, setEndDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Query to fetch current spend limits
  const { data: limitsData, isLoading: limitsLoading } = useQuery({
    queryKey: ["spendLimits", customerId],
    queryFn: async () => {
      const response = await HttpService.getAxiosClient().get(
        `${window.API_URL}/limits/${customerId}/spend/all`
      );
      return response.data;
    },
    enabled: open && !!customerId,
  });

  // Parse limits data when it changes or dialog opens
  useEffect(() => {
    if (open && limitsData && Array.isArray(limitsData)) {
      const dailyLimitObj = limitsData.find(
        (limit) => limit.timeUnit === "Daily"
      );
      const monthlyLimitObj = limitsData.find(
        (limit) => limit.timeUnit === "Monthly"
      );
      const transactionLimitObj = limitsData.find(
        (limit) => limit.timeUnit === "TranAmount"
      );

      // Ensure we only set the numeric value, not the entire object
      const daily = dailyLimitObj?.limitAmount ?? "";
      const monthly = monthlyLimitObj?.limitAmount ?? "";
      const transaction = transactionLimitObj?.limitAmount ?? "";
      const expMins = transactionLimitObj?.expirationMinutes ?? null;
      const pendingStatus = transactionLimitObj?.pendingWorkflowStatus ?? null;

      setDailyLimit(daily === "" ? "" : String(daily));
      setMonthlyLimit(monthly === "" ? "" : String(monthly));
      const tranLimit = transaction === "" ? "" : String(transaction);
      setTransactionLimit(tranLimit);
      setOriginalTransactionLimit(tranLimit);
      setExpirationMinutes(expMins);
      setPendingWorkflowStatus(pendingStatus);
      setEndDate(""); // Reset end date when loading new data
    }
  }, [limitsData, open]);

  // Setup mutation for updating velocity limits
  const velocityMutation = useMutation({
    mutationFn: (data) => {
      return HttpService.getAxiosClient()
        .put(window.API_URL + `/limits/${customerId}/spend`, data)
        .then((response) => response.data);
    },
    onSuccess: (data) => {
      // Refresh limits so UI shows updated values
      queryClient.invalidateQueries(["spendLimits", customerId]);
      const isPending = !!data?.pendingWorkflowStatus;
      
      setFeedback({
        open: true,
        message: isPending 
          ? `Request submitted for approval (Status: ${data.pendingWorkflowStatus}).` 
          : "Spend limits updated successfully (Auto-Approved).",
        severity: isPending ? "info" : "success",
      });

      // Close the dialog after a short delay and reset the form
      setTimeout(() => {
        setIsSubmitting(false);
        resetForm();
        handleClose();
      }, 3500);
    },
    onError: (error) => {
      setIsSubmitting(false);
      console.error("Error updating velocity limits:", error);
      // Prefer server-provided message when available
      const serverData = error?.response?.data;
      const serverMessage =
        (typeof serverData === "string" && serverData) ||
        (serverData &&
          typeof serverData.message === "string" &&
          serverData.message) ||
        null;
      setFeedback({
        open: true,
        message:
          serverMessage || error.message || "Error updating velocity limits",
        severity: "error",
      });
    },
  });

  // Reset form function to clear all inputs and feedback
  const resetForm = () => {
    setDailyLimit("");
    setMonthlyLimit("");
    setTransactionLimit("");
    setOriginalTransactionLimit("");
    setPendingWorkflowStatus(null);
    setEndDate("");
    setIsSubmitting(false);
    setFeedback({
      open: false,
      message: "",
      severity: "success",
    });
  };

  // Override the handleClose function to reset the form
  const handleCloseWithReset = () => {
    resetForm();
    handleClose();
  };

  const handleSubmit = () => {
    // Check if transaction limit has been modified
    const transactionLimitChanged =
      Number(transactionLimit) !== Number(originalTransactionLimit);

    // Validate inputs before submission
    if (!transactionLimit) {
      setFeedback({
        open: true,
        message: "Please enter a transaction limit",
        severity: "error",
      });
      return;
    }

    // If transaction limit changed, require end date
    if (transactionLimitChanged && !endDate) {
      setFeedback({
        open: true,
        message: "Please select an end date for the temporary limit change",
        severity: "error",
      });
      return;
    }

    // Prepare the request data
    const requestData = {
      customerId: customerId,
      transactionLimit: parseFloat(transactionLimit),
    };

    // Add end date if transaction limit changed
    if (transactionLimitChanged && endDate) {
      requestData.endDate = endDate;
    }

    // Execute the mutation
    setIsSubmitting(true);
    velocityMutation.mutate(requestData);
  };

  const handleFeedbackClose = () => {
    setFeedback({ ...feedback, open: false });
  };

  return (
    <Dialog open={open} onClose={handleCloseWithReset} maxWidth="sm" fullWidth>
      <Box
        sx={{
          backgroundColor: "primary.main",
          px: 3,
          minHeight: 52,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CreditCardIcon sx={{ color: "white" }} />
          <Typography variant="h6" sx={{ color: "white" }}>
            {Labels.getLabel("manageCard.velocityLimitsTitle", "Spend Limits")}
          </Typography>
        </Box>
        <IconButton
          onClick={handleCloseWithReset}
          sx={{ color: "white" }}
          disabled={velocityMutation.isPending}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ paddingTop: 3 }}>
        {limitsLoading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Monthly Limit ($)
              </Typography>
              <NumericFormat
                value={monthlyLimit}
                placeholder="Not set"
                fullWidth
                variant="outlined"
                disabled
                InputProps={{
                  readOnly: true,
                }}
                thousandSeparator={true}
                prefix={"$"}
                decimalScale={2}
                fixedDecimalScale={true}
                allowNegative={false}
                customInput={TextField}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Daily Limit ($)
              </Typography>
              <NumericFormat
                value={dailyLimit}
                placeholder="Not set"
                fullWidth
                variant="outlined"
                disabled
                InputProps={{
                  readOnly: true,
                }}
                thousandSeparator={true}
                prefix={"$"}
                decimalScale={2}
                fixedDecimalScale={true}
                allowNegative={false}
                customInput={TextField}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Transaction Limit ($)
              </Typography>
              <NumericFormat
                value={transactionLimit}
                onValueChange={(values) => {
                  const { value } = values;
                  setTransactionLimit(value);
                }}
                placeholder="Enter per transaction spending limit"
                fullWidth
                variant="outlined"
                disabled={velocityMutation.isPending || !!pendingWorkflowStatus}
                thousandSeparator={true}
                prefix={"$"}
                decimalScale={2}
                fixedDecimalScale={true}
                allowNegative={false}
                customInput={TextField}
              />
              {pendingWorkflowStatus && (
                <Box sx={{ mt: 1 }}>
                  <MuiAlert severity="warning">
                    Transaction limit update is processing ({pendingWorkflowStatus}). 
                    You cannot make further changes until it is completed.
                  </MuiAlert>
                </Box>
              )}
              {expirationMinutes && expirationMinutes > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={`Temporary - Expires ${new Date(
                      Date.now() + expirationMinutes * 60000
                    ).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}`}
                    color="warning"
                    size="small"
                    icon={<AccessTimeIcon />}
                  />
                </Box>
              )}
            </Grid>
            {Number(transactionLimit) !== Number(originalTransactionLimit) &&
              transactionLimit && (
                <>
                  <Grid item xs={12}>
                    <MuiAlert severity="info">
                      This is a temporary adjustment to the transaction limit.
                      Once the end date is reached, the limit will automatically
                      revert to the current value of $
                      {Number(originalTransactionLimit || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.
                    </MuiAlert>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                      End Date for Temporary Limit
                    </Typography>
                    <TextField
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      fullWidth
                      variant="outlined"
                      disabled={velocityMutation.isPending}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        min: new Date().toISOString().split("T")[0],
                        max: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000)
                          .toISOString()
                          .split("T")[0],
                      }}
                    />
                  </Grid>
                </>
              )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCloseWithReset}
          color="inherit"
          disabled={velocityMutation.isPending}
        >
          {Labels.getLabel("manageCard.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={
            isSubmitting ||
            velocityMutation.isPending ||
            limitsLoading ||
            !!pendingWorkflowStatus ||
            !transactionLimit ||
            (Number(transactionLimit) !== Number(originalTransactionLimit) && !endDate)
          }
        >
          {isSubmitting || velocityMutation.isPending ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            Labels.getLabel("manageCard.submit", "Submit")
          )}
        </Button>
      </DialogActions>
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleFeedbackClose}
          severity={feedback.severity}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default function ManageCard(props) {
  const [showCreditDialog, setShowCreditDialog] = useState(false);
  const [showDebitDialog, setShowDebitDialog] = useState(false);
  const [showVelocityDialog, setShowVelocityDialog] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // Removed showPendingTransactions state as it's now handled in CardView

  const handleCreditClick = () => {
    setShowCreditDialog(true);
  };

  const handleDebitClick = () => {
    setShowDebitDialog(true);
  };

  const handleVelocityClick = () => {
    setShowVelocityDialog(true);
  };

  // Function to handle successful credit/debit operation
  const handleOperationSuccess = () => {
    // Refresh the MainView component by updating the key
    setRefreshKey((prevKey) => prevKey + 1);
  };

  return (
    <>
      <Dialog
        open={props.open}
        onClose={props.onClose}
        fullWidth={true}
        maxWidth="lg"
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CreditCardIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              {Labels.getLabel("manageCard.managerCard", "Manager Card")}
            </Typography>
          </Box>
          <IconButton
            onClick={() => props.setOpen(false)}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <MainView
            key={refreshKey} // Add key to force refresh when operations complete
            idCustomer={props.memberInfo.idCustomer}
            handleCreditClick={handleCreditClick}
            handleDebitClick={handleDebitClick}
            handleVelocityClick={handleVelocityClick}
            showAdminButtons={true}
          />

          {/* Pending transactions removed from here - now displayed in CardView */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => props.setOpen(false)}>
            {Labels.getLabel("manageCard.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credit and Debit Dialogs */}
      <CreditDialog
        open={showCreditDialog}
        handleClose={() => {
          setShowCreditDialog(false);
          handleOperationSuccess();
          // Form reset is handled inside the CreditDialog component
        }}
        customerId={props.memberInfo?.idCustomer}
      />
      <DebitDialog
        open={showDebitDialog}
        handleClose={() => {
          setShowDebitDialog(false);
          handleOperationSuccess();
          // Form reset is handled inside the DebitDialog component
        }}
        customerId={props.memberInfo?.idCustomer}
      />
      <VelocityLimitsDialog
        open={showVelocityDialog}
        handleClose={() => {
          setShowVelocityDialog(false);
          handleOperationSuccess();
          // Form reset is handled inside the VelocityLimitsDialog component
        }}
        customerId={props.memberInfo?.idCustomer}
      />
    </>
  );
}
